import { BaseWebComponentView } from '../../framework/BaseWebComponentView';
import { WidgetElementFactory } from '../factory/WidgetElementFactory';
import { GridStack } from 'gridstack';
import { BaseWidgetWebComponent } from '../../widget/BaseWidgetWebComponent';
import { CanExportPdf, DashboardType } from '@nirvana/shared/dtos/types/dashboards/common';
import { ReportingPeriod, TimeRangeKind } from '@nirvana/shared/dtos/types/shared/time';
import { Dashboard, ErrorDialogStrings, Widget } from '../../types';
import { dateFormatter, timeRangeToReportingInterval } from '@nirvana/shared/time';
import { DisplayMode, TemplateState } from '../../types/common/types';
import { ElementClass, elementClassListAdd, elementClassListRemove } from '../../utils/htmlElement.utils';
import { BaseWidgetController } from '../../widget/BaseWidgetController';
import { htmlEncode } from '../../utils/escape.utils';
import { WidgetResults } from '../types/types';

export class DashboardView extends BaseWebComponentView {
  private readonly widgetFactory: WidgetElementFactory;
  public readonly widgetsById: {
    [widgetId: string]: {
      gridElement: any;
      options: any;
      widget: BaseWidgetWebComponent;
    };
  } = {};
  public reportingInterval: ReportingPeriod;
  public canExportPdf: CanExportPdf;
  protected translations: any;
  protected commonStrings: any;
  private dialogStrings: any;

  constructor(shadow: ShadowRoot, template: HTMLElement) {
    super(shadow, template);
    this.widgetFactory = new WidgetElementFactory();
    // We need this because we need to bind the correct 'this' to the function,
    // but we also need the function to NOT be anonymous
    // See the comment on refreshDashboard for more info
    this.refreshDashboard = this.refreshDashboard.bind(this);
  }

  getDomElements() {
    this.dom.$title = this.shadow.querySelector('.title');
    this.dom.$pdfTitle = this.shadow.querySelector('.pdf-title');
    this.dom.$widgets = this.shadow.querySelector('.widgets');
    this.dom.$labelCompany = this.shadow.querySelector('.company-title');
    this.dom.$wrapper = this.shadow.querySelector('.wrapper');
    this.dom.$timeRangeDropdown = this.shadow.querySelector('#timeRangeDropdown');
    this.dom.$companySelectorDropdown = this.shadow.querySelector('#companySelectorDropdown');
    this.dom.$refreshButton = this.shadow.querySelector('#dashboard-refresh');
    this.dom.$reportingPeriodTitle = this.shadow.querySelector('.reporting-period-title');
    this.dom.$reportingPeriodInterval = this.shadow.querySelector('.reporting-period-interval');
    this.dom.$dashboardActions = this.shadow.querySelector('#dashboardActions');
    this.dom.$actionsButton = this.shadow.querySelector('#actionsButton');
    this.dom.$actionsButtonName = this.shadow.querySelector('#actionsButtonName');

    this.getTranslations();
    this.setTranslatedStrings();

    this.dom.$grid = GridStack.init(
      {
        float: true,
        animate: false,
        cellHeight: 42,
        minRow: 3,
        draggable: {
          handle: '.grid-stack-item-content',
          appendTo: this.shadow.querySelector('.grid-stack'),
        },
        column: 12,
        removable: false,
      },
      this.shadow.querySelector('.grid-stack')
    );
    this.dom.$refreshButton.addEventListener('click', this.refreshDashboard);
  }

  getTranslations() {
    this.translations = this.stringsTranslator.getDashboardStrings();
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.dialogStrings = this.stringsTranslator.getDialogStrings();
  }

  setTranslatedStrings() {
    this.dom.$reportingPeriodTitle.innerHTML = this.translations.reportingPeriod;
  }

  private buildCompanySelectorDropdown(displayMode: string, isPartner: boolean) {
    switch (displayMode) {
      case DisplayMode.REPORT:
      case DisplayMode.PDF:
        if (!this.dom.$companySelectorDropdown.classList.contains(ElementClass.HIDDEN)) {
          elementClassListAdd(this.dom.$companySelectorDropdown, ElementClass.HIDDEN);
        }
        break;
      case DisplayMode.DASHBOARD:
        if (!isPartner) {
          if (!this.dom.$companySelectorDropdown.classList.contains('hidden')) {
            elementClassListAdd(this.dom.$companySelectorDropdown, ElementClass.HIDDEN);
          }
          return;
        } else {
          elementClassListRemove(this.dom.$companySelectorDropdown, ElementClass.HIDDEN);
        }
        break;
      default:
        throw new Error(`Unknown display mode ${displayMode}`);
    }

    this.dom.$companySelectorDropdown.injectDependencies(this.dependencies);

    this.dom.$companySelectorDropdown.companySelectorParams = {
      includeOwnCompanyIfInTargetsOnlyOwnCompany: true,
      includeOnlyWithFullAccess: true,
    };

    this.dom.$companySelectorDropdown.start().catch(console.error);
  }

  private buildTimeRangeDropdown() {
    const { timeRangeOptions } = this.commonStrings;
    const options = [
      {
        id: 1,
        title: timeRangeOptions.last24h,
        value: '1',
      },
      {
        id: 2,
        title: timeRangeOptions.last7days,
        value: '7',
      },
      {
        id: 3,
        title: timeRangeOptions.last30days,
        value: '30',
      },
    ];

    this.dom.$timeRangeDropdown.setAttribute('options', JSON.stringify(options));
  }

  setLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.LOADING);
  }

  removeLoadingState() {
    this.resetTemplateState(this.dom.$wrapper);
  }

  setDisableForSelectorsAndButtons(disabled: boolean) {
    this.dom.$timeRangeDropdown.disabled = disabled;
    this.dom.$refreshButton.disabled = disabled;

    if (this.canExportPdf === CanExportPdf.HIDDEN) {
      elementClassListAdd(this.dom.$dashboardActions, ElementClass.HIDDEN);
    } else {
      elementClassListRemove(this.dom.$dashboardActions, ElementClass.HIDDEN);
      this.dom.$dashboardActions.disabled = disabled;
    }

    // Setting propertiesSet will re-render the company selector.
    this.dom.$companySelectorDropdown.propertiesSet = false;
    this.dom.$companySelectorDropdown.disabled = disabled;
    this.dom.$companySelectorDropdown.propertiesSet = true;
  }

  // We need this because if we used an anonymous function, the event handler would be added multiple times,
  // causing multiple useless refreshes of the widgets. See:
  // https://developer.mozilla.org/en-US/docs/Web/API/EventTarget/addEventListener#Multiple_identical_event_listeners
  private refreshDashboard() {
    // The refresh and selectors will be re-enabled when all the data is fetched.
    this.setDisableForSelectorsAndButtons(true);

    // Use the same time range from previous requests.
    this.updateReportingIntervalFromNumDays(this.reportingInterval.timeRange);

    const widgets = Object.values(this.widgetsById).map(value => value.widget);
    for (const widget of widgets) {
      widget.propertiesSet = false;
      widget.reportingPeriod = this.reportingInterval;

      // Setting propertiesSet will re-render the widget. Re-rendering will trigger data fetches from the backend,
      // and we'll show loaders while the data is fetched. However, not all the dashboard is refreshed,
      // which is good.
      widget.propertiesSet = true;
    }

    // If we want to refresh all the dashboard, we can do sth like this in the future:
    // globalThis.Application.app.loadedModules.dashboardV2.modulePanel
    //   .down('dashboard-v2-content')
    //   .fireEvent('shouldDashboardReload');
  }

  buildSuccessView(dashboard: Dashboard) {
    if (!dashboard) {
      this.dom.$title.innerHTML = '';
      this.dom.$pdfTitle.innerHTML = '';
      return;
    }

    this.buildDashboardActions(dashboard.hasReportsEnabled);

    const canExportPdf = (dashboard.features || {}).canExportPdf;
    this.canExportPdf = canExportPdf ? canExportPdf : CanExportPdf.HIDDEN;

    switch (dashboard.displayMode) {
      case DisplayMode.REPORT:
        this.buildReportView(dashboard);
        if (!dashboard.reportingPeriod) {
          throw new Error('Expected to have dashboard.reportingPeriod for report mode');
        }
        this.reportingInterval = dashboard.reportingPeriod;
        break;

      case DisplayMode.PDF:
        this.buildPdfView(dashboard);
        if (!dashboard.reportingPeriod) {
          throw new Error('Expected to have dashboard.reportingPeriod for PDF mode');
        }
        this.reportingInterval = dashboard.reportingPeriod;
        this.dom.$reportingPeriodInterval.innerHTML = this.getReportingIntervalString();
        break;

      case DisplayMode.DASHBOARD:
        this.buildDashboardView(dashboard);
        if (
          !dashboard.timeRange ||
          dashboard.timeRange.kind !== TimeRangeKind.RELATIVE_TIME_RANGE ||
          !dashboard.timeRange.relative
        ) {
          throw new Error(`Unexpected time range for dashboard mode: ${JSON.stringify(dashboard.timeRange, null, 2)}`);
        }
        const lastNumDays = dashboard.timeRange.relative.lastNumDays;
        this.dom.$timeRangeDropdown.value = lastNumDays.toString();
        this.updateReportingIntervalFromNumDays(lastNumDays);
        break;

      default:
        throw new Error(`Unknown display mode ${dashboard.displayMode}`);
    }

    this.dom.$grid.on('change', () => {
      try {
        const widgets: { id: number; x: number; y: number; width: number; height: number }[] = [];

        this.dom.$grid.engine.nodes.forEach(node => {
          widgets.push({
            id: node.id,
            x: node.x,
            y: node.y,
            width: node.width,
            height: node.height,
          });
        });

        this.dom.$wrapper.dispatchEvent(
          new CustomEvent('widgetsNewOrder', {
            detail: {
              widgets,
            },
            bubbles: true,
          })
        );
      } catch (e) {
        console.error(e);
      }
    });

    this.dom.$grid.batchUpdate();
    this.setDisableForSelectorsAndButtons(true);
    // Remove existing widgets that are not in the dashboard.
    this.removeUnusedWidgets(dashboard);

    // Update new widgets.
    (dashboard.widgets || []).forEach(widget => {
      this.widgetsById[widget.id]
        ? this.updateExistingWidgetInDashboard(widget, dashboard)
        : this.addWidgetToDashboard(widget, dashboard);
    });

    this.dom.$grid.commit();
  }

  public getWidgetResultsByWidgetId(): WidgetResults {
    const widgetResultsByWidgetId: { [id: string]: any } = {};
    let allWidgetsWithResult = true;
    for (const widgetId of Object.keys(this.widgetsById || {})) {
      const widgetComponent = this.widgetsById[widgetId].widget;
      const widgetResult = widgetComponent.controller
        ? (widgetComponent.controller as BaseWidgetController<any>).lastLoadedResult
        : undefined;
      if (!widgetResult) {
        allWidgetsWithResult = false;
      } else {
        widgetResultsByWidgetId[widgetId] = widgetResult;
      }
    }

    return { results: widgetResultsByWidgetId, allWidgetsWithResult };
  }

  public showExportPdfError(errorMessage: string) {
    const strings: ErrorDialogStrings = {
      title: this.dialogStrings.genericErrorTitle,
      message: htmlEncode(errorMessage),
      actionButtonString: this.dialogStrings.confirm,
    };

    this.buildDialogElement(strings);
  }

  private updateReportingIntervalFromNumDays(lastNumDays: number) {
    const timezone = this.dependencies.getTimezone();
    const interval = timeRangeToReportingInterval(lastNumDays, timezone);

    this.reportingInterval = interval;
    this.dom.$reportingPeriodInterval.innerHTML = this.getReportingIntervalString();
  }

  private getReportingIntervalString(): string {
    if (!this.reportingInterval) {
      return '';
    }

    // format the intervals and display them in the template
    if (!this.reportingInterval.previous.toTimestamp || !this.reportingInterval.current.toTimestamp) {
      throw new Error('Missing reporting interval margins.');
    }

    const timezone = this.dependencies.getTimezone();
    const intervalStart = dateFormatter(this.reportingInterval.previous.toTimestamp, timezone);
    const intervalEnd = dateFormatter(this.reportingInterval.current.toTimestamp, timezone);

    return `${intervalStart} - ${intervalEnd}`;
  }

  private addWidgetToDashboard(widget: Widget, dashboard: Dashboard) {
    if (!dashboard.displayMode) {
      throw new Error('Programmatic error: missing display mode');
    }

    const $widget = this.widgetFactory.build<BaseWidgetWebComponent>(widget.type);
    $widget.injectDependencies(this.dependencies);
    $widget.dashboardId = dashboard.id;
    $widget.widgetId = widget.id;
    $widget.widgetType = this.widgetFactory.getType(widget.type);
    $widget.targets = [dashboard.selectedTarget];
    $widget.reportingPeriod = this.reportingInterval;
    $widget.canDrillDown = dashboard.canDrillDown || false;
    $widget.displayMode = dashboard.displayMode;

    const $mockDiv = document.createElement('div');
    $mockDiv.setAttribute('class', 'grid-stack-item-content item-content');
    $mockDiv.appendChild($widget);

    const $item = document.createElement('div');
    $item.classList.add('item', `item-type-${widget.type}`);
    $item.appendChild($mockDiv);

    const options = {
      w: widget.width,
      h: widget.height,
      maxW: widget.width,
      maxH: widget.height,
      minW: widget.width,
      minH: widget.height,
      locked: true,
      noResize: true,
      noMove: true,
      id: `${widget.id}`,
    };
    if (widget.pos) {
      options['x'] = widget.pos.x;
      options['y'] = widget.pos.y;
    }
    this.dom.$grid.addWidget($item, options);

    this.widgetsById[widget.id] = {
      gridElement: $item,
      options: options,
      widget: $widget,
    };

    $widget.start().then().catch();
  }

  private updateExistingWidgetInDashboard(widget: Widget, dashboard: Dashboard) {
    const existingWidget = this.widgetsById[widget.id].widget;

    let hasNewAttributes = false;

    existingWidget.propertiesSet = false;

    if (JSON.stringify(existingWidget.reportingPeriod) !== JSON.stringify(this.reportingInterval)) {
      hasNewAttributes = true;

      existingWidget.reportingPeriod = this.reportingInterval;
    }

    if (JSON.stringify(existingWidget.targets) !== JSON.stringify([dashboard.selectedTarget])) {
      hasNewAttributes = true;
      existingWidget.targets = [dashboard.selectedTarget];
    }

    if (existingWidget.canDrillDown !== (dashboard.canDrillDown || false)) {
      hasNewAttributes = true;
      existingWidget.canDrillDown = dashboard.canDrillDown || false;
    }

    if (hasNewAttributes) {
      existingWidget.propertiesSet = true;
    }
  }

  private removeUnusedWidgets(newDashboard: Dashboard) {
    const newWidgetIds = {};
    (newDashboard.widgets || []).forEach(widget => {
      newWidgetIds[widget.id] = true;
    });
    const existingWidgetIds = Object.keys(this.widgetsById);
    for (const existingWidgetId of existingWidgetIds) {
      if (!newWidgetIds[existingWidgetId]) {
        const { gridElement } = this.widgetsById[existingWidgetId];
        this.dom.$grid.removeWidget(gridElement);
        delete this.widgetsById[existingWidgetId];
      }
    }
  }

  private getTranslatedTitle(dashboard: Dashboard) {
    switch (dashboard.type) {
      case DashboardType.EXECUTIVE_SUMMARY:
        return this.translations.title;
      case DashboardType.CUSTOM:
        // Do not translate the title of custom dashboards.
        return dashboard.title;
      default:
        throw new Error(`Unknown dashboard type: ${dashboard.type}`);
    }
  }

  buildTimeRangeSelectorErrorDialog() {
    const dialogStrings = this.stringsTranslator.getDialogStrings();
    const { genericErrorTitle, timeRangeErrorMessage, confirm } = dialogStrings;

    const strings: ErrorDialogStrings = {
      title: genericErrorTitle,
      message: timeRangeErrorMessage,
      actionButtonString: confirm,
    };

    this.buildDialogElement(strings);
  }

  private buildReportView(dashboard: Dashboard) {
    this.canExportPdf = CanExportPdf.HIDDEN;
    this.dom.$title.innerHTML = '';
    this.dom.$pdfTitle.innerHTML = '';
    this.buildCompanySelectorDropdown(DisplayMode.REPORT, dashboard.isPartner);

    if (!this.dom.$labelCompany.classList.contains(ElementClass.HIDDEN)) {
      elementClassListAdd(this.dom.$labelCompany, ElementClass.HIDDEN);
    }
  }

  private buildPdfView(dashboard: Dashboard) {
    this.canExportPdf = CanExportPdf.HIDDEN;
    this.dom.$title.innerHTML = this.getTranslatedTitle(dashboard);
    this.dom.$pdfTitle.innerHTML = this.getTranslatedTitle(dashboard);
    this.dom.$labelCompany.innerText = this.dom.$companySelectorDropdown.selectedTarget.companyName;

    if (this.dom.$labelCompany.classList.contains(ElementClass.HIDDEN)) {
      elementClassListRemove(this.dom.$labelCompany, ElementClass.HIDDEN);
    }

    this.buildCompanySelectorDropdown(DisplayMode.PDF, dashboard.isPartner);
  }

  private buildDashboardView(dashboard: Dashboard) {
    this.buildCompanySelectorDropdown(DisplayMode.DASHBOARD, dashboard.isPartner);
    this.buildTimeRangeDropdown();
    this.dom.$title.innerHTML = this.getTranslatedTitle(dashboard);
    this.dom.$pdfTitle.innerHTML = this.getTranslatedTitle(dashboard);

    if (!this.dom.$labelCompany.classList.contains(ElementClass.HIDDEN)) {
      elementClassListAdd(this.dom.$labelCompany, ElementClass.HIDDEN);
    }
  }

  private buildDashboardActions(hasReportsEnabled: boolean) {
    this.dom.$actionsButtonName.textContent = this.translations.actions;

    const options = [
      {
        title: this.dependencies.translateFn('Export PDF'),
        value: 0,
      },
    ];

    if (hasReportsEnabled) {
      options.push({
        title: this.dependencies.translateFn('Create report'),
        value: 1,
      });
    }

    this.dom.$dashboardActions.options = options;
  }
}
